'use strict'

import CXPointsArray from './CXPointsArray.js'
import CXPointsArraySimple from './CXPointsArraySimple.js'
import CXPointsResultArray from './CXPointsResultArray.js'
import Config from '../Config.js'

/**
 * Flood-fill style image segmentation engine used by the magic-wand tool.
 * The processor stores frontier points per tolerance level and returns
 * index maps so the UI can update sensitivity without recomputing everything.
 */
class ImageSegmentationProcessor {
    /**
     * @param {number} initMaxValue
     * @param {Uint8ClampedArray} imgColorOriginal
     * @param {number} w
     * @param {number} h
     */
    constructor(initMaxValue, imgColorOriginal, w, h) {
        this.DEFAULT_MAX_LEVEL = initMaxValue
        this.KERNEL_SIZE = 1

        this.pointsInTheRegion = new CXPointsResultArray()
        this.fronterPoints = new CXPointsArray()
        this.pointsToTreat = new CXPointsArraySimple()

        this.actualMaxColor = 0
        this.selectedRGBColor = []
        this.calculationLevel = 0
        this.imgGray
        this.isProcessing = false

        this.imgColorOriginal = imgColorOriginal
        this.width = w
        this.height = h

        this.clickedPoint

        this.newPosition = {
            x: 0,
            y: 0,
            l: 0,
            s: false,
        }

        this.pixelPosition = {
            x: 0,
            y: 0,
            l: 0,
            s: false,
        }
    }

    /**
     * Initialize a fresh grayscale mask buffer.
     * @returns {void}
     */
    createEmptyImageGray() {
        this.imgGray = new Array()

        // Create columns (width)
        for (let i = 0; i < this.width; i++) {
            this.imgGray[i] = new Array()
        }

        // Create rows
        for (let i = 0; i < this.width; i++) {
            for (let j = 0; j < this.height; j++) {
                this.imgGray[i].push(Config.DEFAULT_MASKING_COLOR)
            }
        }
    }

    /**
     * Populate grayscale mask values from a linear mask array.
     * @param {Uint8ClampedArray|number[]} imgGray
     * @returns {void}
     */
    setImageGray(imgGray) {
        for (let i = 0; i < this.width; i++) {
            for (let j = 0; j < this.height; j++) {
                // console.log("xy: "+ i +":" + j +"  pos: " + (i * this.width + j) + " val: " + imgGray[i * this.width + j]);
                if (imgGray[i + this.width * j] !== Config.DEFAULT_MASKING_COLOR) {
                    this.imgGray[i][j] = 0
                } else {
                    this.imgGray[i][j] = Config.DEFAULT_MASKING_COLOR
                }
            }
        }
    }

    /**
     * Save click coordinates used as flood-fill seed point.
     * @param {{x:number,y:number}} clickColor
     * @returns {void}
     */
    receivedImageFromClick(clickColor) {
        // Convert the position in our point coordinate
        this.clickedPoint = {
            x: clickColor.x,
            y: clickColor.y,
        }
    }

    /**
     * Draw the first segmented surface for a given sensitivity value.
     * @param {number} sliderValue
     * @returns {Map<number,number[]>}
     */
    drawFirstSurfaceAtPosition(sliderValue) {
        this.isProcessing = true

        this.selectedRGBColor = this.getRGBColorAtPosition(this.clickedPoint)

        // Reset the level of processing
        this.calculationLevel = sliderValue

        // Reset the pointsInTheRegion
        this.pointsInTheRegion = new CXPointsResultArray(this.width)

        // Reset the _fronterPoints
        this.fronterPoints = new CXPointsArray(~~((this.width * this.height) / 10.0))

        // Reset the pointsToTreat
        this.pointsToTreat = new CXPointsArraySimple()

        // Add clicked point in Region
        this.pointsInTheRegion.addPoint(this.clickedPoint.x, this.clickedPoint.y)

        // Add clicked point in Fronter
        this.fronterPoints.addPoint(this.clickedPoint.x, this.clickedPoint.y)

        // Set the pixel selected of the masking
        this.setGrayColor(this.clickedPoint)

        // Get all the points for the level
        let sizeOfTheRegionInPixels = 0

        // Threshold color
        this.actualMaxColor = Math.max(3, Math.min(this.calculationLevel - 20, 255))

        // Get all the points for the level
        sizeOfTheRegionInPixels = this.fronterPoints.count()

        // reset array
        this.pointsToTreat = new CXPointsArraySimple()

        for (let i = 0; i < sizeOfTheRegionInPixels; i++) {
            const aPoint = this.fronterPoints.objectAtIndex(i)
            this.pointsToTreat.addPoint(aPoint.x, aPoint.y, this.calculationLevel, true)

            // Compute points
            this.getRegionFromPointInRegion()
        }

        return this.pointsInTheRegion.pointsArray
    }

    drawSelectSurfaceAtPosition() {
        // console.time("drawSelectSurfaceAtPosition");

        this.selectedRGBColor = this.getRGBColorAtPosition(this.clickedPoint)

        // Reset the level of processing
        this.calculationLevel = 0

        // Reset the pointsInTheRegion
        this.pointsInTheRegion = new CXPointsResultArray(this.width)

        // Reset the _fronterPoints
        this.fronterPoints = new CXPointsArray(~~((this.width * this.height) / 10.0))

        // Reset the pointsToTreat
        this.pointsToTreat = new CXPointsArraySimple()

        // Add clicked point in Region
        this.pointsInTheRegion.addPoint(this.clickedPoint.x, this.clickedPoint.y)

        // Add clicked point in Fronter
        this.fronterPoints.addPoint(this.clickedPoint.x, this.clickedPoint.y, this.calculationLevel, true)

        // Set the pixel selected of the masking
        this.setGrayColor(this.clickedPoint)

        let arrayIndexes = [0]

        // Get all the points for the level
        let sizeOfTheRegionInPixels = 0

        // Compute each levels // here we go until <= !!
        while (this.calculationLevel <= this.DEFAULT_MAX_LEVEL) {
            // Level
            this.calculationLevel += 1

            // Threshold color
            this.actualMaxColor = Math.max(3, Math.min(this.calculationLevel - 20, 255))

            // Get all the points for the level
            sizeOfTheRegionInPixels = this.fronterPoints.count()

            // reset array
            this.pointsToTreat = new CXPointsArraySimple()

            for (let i = 0; i < sizeOfTheRegionInPixels; i++) {
                const aPoint = this.fronterPoints.objectAtIndex(i)
                this.pointsToTreat.addPoint(aPoint.x, aPoint.y, this.calculationLevel, true)

                // Compute points
                this.getRegionFromPointInRegion()
            }

            // Store max size foreach level
            arrayIndexes.push(this.pointsInTheRegion.pointsArray.size)

            // Remove range of points from fronterPoints array
            this.fronterPoints.removeFirstPointToSize(sizeOfTheRegionInPixels)
        }

        // console.timeEnd("drawSelectSurfaceAtPosition");

        // return array with all frontier points and array with index according to each levels
        return [this.pointsInTheRegion.pointsArray, arrayIndexes]
    }

    checkIfPointInRegion(position, basePosition) {
        // Check that the position is in the frame
        if (position.x >= 0 && position.x < this.width && position.y >= 0 && position.y < this.height) {
            // If not painted => 1 else 0
            let isPixelNotPainted = this.getGrayColorAtPosition(position) === Config.DEFAULT_MASKING_COLOR

            // Check that the pixel is not used for other colors and that it is the actual range of colors
            if (isPixelNotPainted && this.deltaErgb(this.selectedRGBColor, this.getRGBColorAtPosition(position)) < this.actualMaxColor) {
                // Set color mask
                this.setGrayColor(position)

                // Add the point to treat later
                this.pointsToTreat.addPoint(position.x, position.y, position.l, position.s)

                // Add the point to the region of points
                this.pointsInTheRegion.addPoint(position.x, position.y)
            } else if (isPixelNotPainted) {
                basePosition.s = false
            }
        }
    }

    getRegionFromPointInRegion() {
        while (this.pointsToTreat.count() > 0) {
            this.pixelPosition = this.pointsToTreat.firstObject()

            this.newPosition.l = this.pixelPosition.l + 1

            // East position
            this.newPosition.x = this.pixelPosition.x + this.KERNEL_SIZE
            this.newPosition.y = this.pixelPosition.y
            this.checkIfPointInRegion(this.newPosition, this.pixelPosition)

            // North position
            this.newPosition.y += this.KERNEL_SIZE
            this.newPosition.x -= this.KERNEL_SIZE
            this.checkIfPointInRegion(this.newPosition, this.pixelPosition)

            // West position
            this.newPosition.x -= this.KERNEL_SIZE
            this.newPosition.y -= this.KERNEL_SIZE
            this.checkIfPointInRegion(this.newPosition, this.pixelPosition)

            // South position
            this.newPosition.y -= this.KERNEL_SIZE
            this.newPosition.x += this.KERNEL_SIZE
            this.checkIfPointInRegion(this.newPosition, this.pixelPosition)

            if (!this.pixelPosition.s) {
                this.fronterPoints.addPoint(this.pixelPosition.x, this.pixelPosition.y, this.pixelPosition.l, this.pixelPosition.s)
            }

            this.pointsToTreat.removeFirstPoint()
        }
    }

    // ------------------------------------------------------------------------------------------------
    // UTILS METHODS
    // ------------------------------------------------------------------------------------------------

    getGrayColorAtPosition(point) {
        return this.imgGray[point.x][point.y]
    }

    getRGBColorAtPosition(p) {
        let position = (p.x + this.width * p.y) * 4
        return {
            r: this.imgColorOriginal[position],
            g: this.imgColorOriginal[position + 1],
            b: this.imgColorOriginal[position + 2],
        }
    }

    setGrayColor(point) {
        this.imgGray[point.x][point.y] = 0
    }

    deltaErgb(color1, color2) {
        return Math.sqrt(Math.pow(color1.r - color2.r, 2.0) + Math.pow(color1.g - color2.g, 2.0) + Math.pow(color1.b - color2.b, 2.0))
    }
}
export default ImageSegmentationProcessor
